import type { StoreData } from "@features/stores/types";
import type { StoreLocatorLocation } from "@components/store-locator/controller";
import { CommonCMS, ClickAndCollect, ShoppingBag } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { usePickupStoreSelection } from "@components/click-and-collect/usePickupStoreSelection";
import { useStore, useStoreApi } from "@store/store";
import type { StoreState } from "@store/types";
import { focusOutline } from "@ecco/ui/css/focus";
import { Button } from "@ecco/ui/button";
import { Radio } from "@ecco/ui/radio";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
} from "@ecco/ui/modal";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import type { StoreApi } from "zustand";
import type { DeliveryStepFormProps } from "../types";
import { Text } from "@ecco/ui/text";
import { DeliveryFee } from "./DeliveryFee";
import { activeSiteConfig, activeSiteCountry } from "@config/site/site-config";
import { SelectedStoreData } from "./SelectedStoreData";
import { ShippingMethodTypes } from "@features/checkout/types";
import ShippingMethods from "@lib/constants/contentful/shipping-methods";
import { Heading } from "@ecco/ui/heading";
import { CarrierIcon } from "./carrier-icon/CarrierIcon";
import { checkClickAndCollectAvailability } from "@components/commerce/helpers";
import { CheckoutAvailabilityIndicator } from "../CheckoutAvailabilityIndicator";
import { useStores } from "@features/stores/client";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import {
    useEstimatedDeliveryDate,
    useExpectedDeliveryText,
    useIsEstimatedDeliveryDateLoading,
} from "@hooks/useEstimatedDeliveryDate";
import { DeliveryEstimate } from "./DeliveryEstimate";
import { CheckoutStoreLocator } from "../CheckoutStoreLocator";
import {
    ESTIMATED_DELIVERY_HEADING,
    ESTIMATED_DELIVERY_HEADING_VB,
} from "@lib/utils/pdp/estimatedDelivery";

interface RadioButtonPickUpPointProps {
    method: DeliveryStepFormProps["shippingMethods"][0];
    storeData: StoreData;
    locale: string;
}

export const RadioButtonPickUpPoint = ({
    method,
    storeData,
    locale,
}: RadioButtonPickUpPointProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const onStoreSelected = usePickupStoreSelection(method.key as ShippingMethodTypes);
    const api: StoreApi<StoreState> = useStoreApi();
    const cart = useStore((state) => state.data.me.cartInfo.cart);
    const isOmnichannelMarket = useFeatureFlag("isOmnichannelMarket");

    const lineItems = api.getState().data.me.cartInfo.cart.lineItems;
    const { getMultiple } = useMicrocopy();
    const isAvailableNow =
        cart && storeData ? checkClickAndCollectAvailability(cart, storeData) : false;

    const { data: storesData = [] } = useStores({
        country: activeSiteConfig.country,
        locale: locale,
        queryConfig: {
            enabled: isOmnichannelMarket,
        },
    });

    const storesWithStock = useMemo(() => {
        if (method.key === ShippingMethodTypes.PARCEL_SHOP) {
            return [];
        }
        // Filtering the stores that are available for PickupInStore
        return storesData.filter(
            (store) => store?.pickupInStore === true && store.countryCode === activeSiteCountry
        );
    }, [storesData, method.key]);

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.headline,
            ClickAndCollect.selectStore,
            ClickAndCollect.changeStore,
            ClickAndCollect.available,
            ClickAndCollect.availableSoon,
            ShippingMethods.parcelShops.headline,
            ShippingMethods.parcelShops.selectPickUpPoint,
            ShippingMethods.parcelShops.changePickUpPoint,
        ]);
    })();

    const isParcelShop = method.key === ShippingMethodTypes.PARCEL_SHOP;
    const isClickAndCollect = method.key === ShippingMethodTypes.CLICK_AND_COLLECT;
    const location: StoreLocatorLocation = isParcelShop
        ? "checkout.parcelShop"
        : "checkout.clickAndCollect";
    const headline = isParcelShop
        ? microcopies[ShippingMethods.parcelShops.headline]
        : microcopies[ClickAndCollect.headline];

    const estimatedDeliveryDate = useEstimatedDeliveryDate();
    // Only Parcel Shop has a shipping estimate; Click & Collect never does.
    const isParcelShopFulfillment = method.fulfillmentOption === "PS";
    const expectedDeliveryText = useExpectedDeliveryText(
        isParcelShopFulfillment ? estimatedDeliveryDate : null
    );

    const expectedDeliveryLabelVB = useExpectedDeliveryText(
        isParcelShopFulfillment ? estimatedDeliveryDate : null,
        ShoppingBag.expectedDeliveryVB
    );

    const isEstimatedDeliveryDateLoading =
        useIsEstimatedDeliveryDateLoading() && isParcelShopFulfillment;
    // The raw carrier copy (e.g. "3-4 business days") is only a fallback for when we can't show
    // an actual estimated date - once we can, showing both is redundant, so the estimate (or
    // its loading skeleton) takes over.
    const showsDeliveryEstimate = Boolean(expectedDeliveryText) || isEstimatedDeliveryDateLoading;

    const header = (
        <Heading as="h2" textTransform={"uppercase"} color="black" fontSize="desktopBodyTextNormal">
            {headline}
        </Heading>
    );

    const getButtonText = () => {
        if (isEmpty(storeData)) {
            return isParcelShop
                ? microcopies[ShippingMethods.parcelShops.selectPickUpPoint]
                : microcopies[ClickAndCollect.selectStore];
        }
        return isParcelShop
            ? microcopies[ShippingMethods.parcelShops.changePickUpPoint]
            : microcopies[ClickAndCollect.changeStore];
    };

    const deliveryTime = method.deliveryTime[locale];
    const deliveryPrice = method.deliveryFee ?? method.price?.totalNet;
    const name = method.localizedName?.[locale] ?? method.name;
    const carrierCode = method.hasMultipleIcons
        ? "multipleIcons"
        : method.carrierCode?.toLowerCase();

    const iconName = isParcelShop ? carrierCode : "click-and-collect";

    return (
        <Box onClick={onOpen} gap={2}>
            <Flex flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"}>
                <Radio
                    value={method.key}
                    onClick={(e) => {
                        // Prevent user from changing the value to CC by clicking
                        e.preventDefault();
                    }}
                    onKeyDown={(e) => {
                        // Change the delivery method on Enter or Space key press
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onOpen();
                        }
                    }}
                    bg={"white"}
                    tabIndex={0}
                    alignItems={"center"}
                    height="auto"
                    flex={1}
                >
                    <Flex
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        gap={2}
                    >
                        <CarrierIcon name={iconName} />
                        <Flex
                            gap={1}
                            flexDir="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                        >
                            {name && <Text fontWeight={"bold"}>{name}</Text>}
                            {deliveryTime && !showsDeliveryEstimate && (
                                <Text size="small">{deliveryTime}</Text>
                            )}
                            <>
                                <Flex data-ab-id={ESTIMATED_DELIVERY_HEADING}>
                                    <DeliveryEstimate
                                        label={expectedDeliveryText}
                                        isLoading={isEstimatedDeliveryDateLoading}
                                    />
                                </Flex>
                                <Flex
                                    data-ab-id={ESTIMATED_DELIVERY_HEADING_VB}
                                    className="ab-hide"
                                >
                                    <DeliveryEstimate
                                        label={expectedDeliveryLabelVB}
                                        isLoading={isEstimatedDeliveryDateLoading}
                                    />
                                </Flex>
                            </>
                            {!isEmpty(storeData) && <SelectedStoreData storeData={storeData} />}
                            <Button
                                fontWeight="normal"
                                variant="link"
                                h="auto"
                                _focusVisible={{
                                    boxShadow: "0 0 0 1px black",
                                }}
                            >
                                {getButtonText()}
                            </Button>
                            {isClickAndCollect && !isEmpty(storeData) && (
                                <CheckoutAvailabilityIndicator isAvailableNow={isAvailableNow} />
                            )}
                        </Flex>
                    </Flex>
                </Radio>
                {activeSiteConfig.checkout.hasShippingFees && deliveryPrice && (
                    <Flex width="100px" justifyContent="flex-end">
                        <DeliveryFee price={deliveryPrice} locale={locale} />
                    </Flex>
                )}
            </Flex>

            <Modal size="full" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {header}
                        <ModalCloseButton _focusVisible={focusOutline} />
                    </ModalHeader>
                    <ModalBody>
                        <CheckoutStoreLocator
                            location={location}
                            onClose={onClose}
                            selectedProductData={lineItems}
                            storesWithStock={storesWithStock}
                            shippingMethodKey={method.key as ShippingMethodTypes}
                            onStoreSelected={onStoreSelected}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};
